import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  FloorPlanState,
  FloorPlanBounds,
  FloorPlanTransform,
  DxfLayer,
  DxfPolylineMeta,
  DxfTextLabel,
  DrawingCalibration,
} from '../../types';

const initialState: FloorPlanState = {
  name: null,
  floorPlanId: null,
  polylines: [],
  bounds: null,
  transform: null,
  scaleFactor: 1,
  dxfUnit: 'mm',
  dxfUnitsPerMeter: 1000,
  calibration: null,
  millimetersPerCanvasUnit: null,
  canvasUnitsPerMeter: null,
  visible: true,
  opacity: 0.85,
  showLabels: true,
  layers: [],
  polylineLayers: [],
  polylineMeta: [],
  labels: [],
  hiddenEntityTypes: {},
  focusedPolylineIndex: null,
  hoveredLayerIndex: null,
  layerVisibilityBaseline: [],
  dirtyLayerIndices: [],
};

function captureVisibilityBaseline(layers: DxfLayer[]): boolean[] {
  return layers.map((layer) => layer.visible !== false);
}

function recomputeDirtyLayerIndices(state: FloorPlanState): void {
  const dirty: number[] = [];
  state.layers.forEach((layer, index) => {
    const baseline = state.layerVisibilityBaseline[index];
    if (baseline === undefined) return;
    if ((layer.visible !== false) !== baseline) {
      dirty.push(index);
    }
  });
  state.dirtyLayerIndices = dirty;
}

const floorplanSlice = createSlice({
  name: 'floorplan',
  initialState,
  reducers: {
    setFloorPlan: (
      state,
      action: PayloadAction<{
        name: string;
        floorPlanId?: string | null;
        polylines: number[][];
        bounds: FloorPlanBounds;
        scaleFactor: number;
        dxfUnit?: 'mm' | 'cm' | 'm' | 'in' | 'ft';
        dxfUnitsPerMeter?: number;
        calibration?: DrawingCalibration | null;
        millimetersPerCanvasUnit?: number | null;
        canvasUnitsPerMeter?: number | null;
        layers?: DxfLayer[];
        polylineLayers?: number[];
        polylineMeta?: DxfPolylineMeta[];
        labels?: DxfTextLabel[];
        transform?: FloorPlanTransform;
      }>
    ) => {
      state.name = action.payload.name;
      state.floorPlanId =
        typeof action.payload.floorPlanId === 'string' && action.payload.floorPlanId.trim()
          ? action.payload.floorPlanId.trim()
          : null;
      state.polylines = action.payload.polylines;
      state.bounds = action.payload.bounds;
      state.transform = action.payload.transform ?? null;
      state.scaleFactor = action.payload.scaleFactor;
      state.dxfUnit = action.payload.dxfUnit ?? 'mm';
      state.dxfUnitsPerMeter = action.payload.dxfUnitsPerMeter ?? 1000;
      state.calibration = action.payload.calibration ?? null;
      state.millimetersPerCanvasUnit = action.payload.millimetersPerCanvasUnit ?? null;
      state.canvasUnitsPerMeter = action.payload.canvasUnitsPerMeter ?? null;
      state.visible = true;
      state.layers = action.payload.layers ?? [
        { name: 'Floor plan', color: 7, visible: true, count: action.payload.polylines.length },
      ];
      state.polylineLayers =
        action.payload.polylineLayers ?? new Array(action.payload.polylines.length).fill(0);
      state.polylineMeta = action.payload.polylineMeta ?? [];
      state.labels = action.payload.labels ?? [];
      state.hiddenEntityTypes = {};
      state.focusedPolylineIndex = null;
      state.hoveredLayerIndex = null;
      state.layerVisibilityBaseline = captureVisibilityBaseline(state.layers);
      state.dirtyLayerIndices = [];
    },
    clearFloorPlan: () => initialState,
    setFloorPlanVisible: (state, action: PayloadAction<boolean>) => {
      state.visible = action.payload;
    },
    toggleFloorPlanVisible: (state) => {
      state.visible = !state.visible;
    },
    setFloorPlanOpacity: (state, action: PayloadAction<number>) => {
      const next = Number.isFinite(action.payload) ? action.payload : state.opacity;
      state.opacity = Math.min(1, Math.max(0, next));
    },
    setLabelsVisible: (state, action: PayloadAction<boolean>) => {
      state.showLabels = action.payload;
    },
    toggleLabelsVisible: (state) => {
      state.showLabels = !state.showLabels;
    },
    toggleDxfLayerVisible: (state, action: PayloadAction<number>) => {
      const layer = state.layers[action.payload];
      if (layer) {
        layer.visible = !layer.visible;
        recomputeDirtyLayerIndices(state);
      }
    },
    setDxfLayerVisible: (
      state,
      action: PayloadAction<{ index: number; visible: boolean }>
    ) => {
      const layer = state.layers[action.payload.index];
      if (layer) {
        layer.visible = action.payload.visible;
        recomputeDirtyLayerIndices(state);
      }
    },
    setAllDxfLayersVisible: (state, action: PayloadAction<boolean>) => {
      state.layers.forEach((l) => {
        l.visible = action.payload;
      });
      recomputeDirtyLayerIndices(state);
    },
    /** After a successful visibility POST, treat current flags as the new baseline. */
    markDxfLayerVisibilitySaved: (state) => {
      state.layerVisibilityBaseline = captureVisibilityBaseline(state.layers);
      state.dirtyLayerIndices = [];
    },
    /** Revert layer visibility to the last saved/load baseline and clear dirty flags. */
    discardDxfLayerVisibilityChanges: (state) => {
      state.layers.forEach((layer, index) => {
        const baseline = state.layerVisibilityBaseline[index];
        if (baseline !== undefined) {
          layer.visible = baseline;
        }
      });
      state.dirtyLayerIndices = [];
      state.focusedPolylineIndex = null;
      state.hoveredLayerIndex = null;
    },
    /** Toggle visibility of a specific entity type within a layer. */
    toggleEntityTypeVisible: (
      state,
      action: PayloadAction<{ layerIndex: number; type: string }>
    ) => {
      const key = `${action.payload.layerIndex}:${action.payload.type}`;
      if (state.hiddenEntityTypes[key]) {
        delete state.hiddenEntityTypes[key];
      } else {
        state.hiddenEntityTypes[key] = true;
      }
    },
    /** Explicitly set visibility for a specific entity type in a layer. */
    setEntityTypeVisible: (
      state,
      action: PayloadAction<{ layerIndex: number; type: string; visible: boolean }>
    ) => {
      const key = `${action.payload.layerIndex}:${action.payload.type}`;
      if (action.payload.visible) {
        delete state.hiddenEntityTypes[key];
      } else {
        state.hiddenEntityTypes[key] = true;
      }
    },
    /** Show or hide every entity type in a given layer. */
    setAllEntityTypesInLayerVisible: (
      state,
      action: PayloadAction<{ layerIndex: number; types: string[]; visible: boolean }>
    ) => {
      action.payload.types.forEach((type) => {
        const key = `${action.payload.layerIndex}:${type}`;
        if (action.payload.visible) {
          delete state.hiddenEntityTypes[key];
        } else {
          state.hiddenEntityTypes[key] = true;
        }
      });
    },
    /** Focus a specific polyline (from a canvas click). Null to clear. */
    setFocusedPolyline: (state, action: PayloadAction<number | null>) => {
      state.focusedPolylineIndex = action.payload;
    },
    /** Set the hovered layer index. Null to clear. */
    setHoveredLayer: (state, action: PayloadAction<number | null>) => {
      state.hoveredLayerIndex = action.payload;
    },
  },
});

export const {
  setFloorPlan,
  clearFloorPlan,
  setFloorPlanVisible,
  toggleFloorPlanVisible,
  setFloorPlanOpacity,
  setLabelsVisible,
  toggleLabelsVisible,
  toggleDxfLayerVisible,
  setDxfLayerVisible,
  setAllDxfLayersVisible,
  markDxfLayerVisibilitySaved,
  discardDxfLayerVisibilityChanges,
  toggleEntityTypeVisible,
  setEntityTypeVisible,
  setAllEntityTypesInLayerVisible,
  setFocusedPolyline,
  setHoveredLayer,
} = floorplanSlice.actions;

export default floorplanSlice.reducer;
